// src/config/upload-config.js
export const UPLOAD_CONFIG = {
    // 图片上传配置 - 稳定优先策略
    image: {
        chunkSize: 2 * 1024 * 1024,    // 2MB 分片大小（平衡传输效率和稳定性）
        maxConcurrent: 3,              // 单文件最大3个分片并发（温和的并发控制）
        retryDelay: 1000,              // 重试延迟 1秒
        maxRetries: 3,                 // 最大重试次数
        timeout: 30000,                // 30秒超时
        pollInterval: 2000,            // 状态轮询间隔 2秒

        // 稳定性优先的设计理念
        strategy: 'stability-first',   // 稳定性优先，而非速度优先
        fileProcessing: 'sequential',  // 文件顺序处理，避免带宽争抢

        // 游客限制
        guest: {
            maxFiles: 15,                // 最多15张图片
            maxFileSize: 5 * 1024 * 1024 // 单个文件最大5MB
        },

        // 支持的格式
        supportedFormats: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
        supportedMimeTypes: ['image/jpeg', 'image/png', 'image/webp']
    },

    // 视频上传配置 - 更加保守的策略
    video: {
        chunkSize: 5 * 1024 * 1024,    // 5MB 分片大小（视频文件较大）
        maxConcurrent: 2,              // 更保守的并发数（视频文件大，网络压力大）
        retryDelay: 1500,              // 更长的重试延迟
        maxRetries: 5,                 // 更多重试次数（视频上传更容易失败）
        timeout: 60000,                // 60秒超时（视频分片传输时间长）
        pollInterval: 3000,            // 状态轮询间隔 3秒

        // 视频上传的特殊考虑
        strategy: 'ultra-stable',      // 超稳定策略
        fileProcessing: 'sequential',  // 严格顺序处理
        networkFriendly: true,         // 网络友好模式

        // 游客限制
        guest: {
            maxFiles: 1,                 // 最多1个视频
            maxFileSize: 60 * 1024 * 1024 // 单个文件最大60MB
        },

        // 支持的格式
        supportedFormats: ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.mkv'],
        supportedMimeTypes: ['video/mp4', 'video/avi', 'video/quicktime', 'video/x-ms-wmv', 'video/x-flv', 'video/x-matroska']
    },

    // 网络配置 - 保守的自适应策略
    network: {
        // 根据网络状况动态调整（但保持保守）
        adaptiveConcurrency: true,

        // 网络质量阈值（更保守的调整策略）
        networkQuality: {
            excellent: { successRate: 0.98, concurrencyBonus: 1 },    // 只有在极好的网络下才增加并发
            good: { successRate: 0.90, concurrencyBonus: 0 },         // 好网络保持默认
            poor: { successRate: 0.80, concurrencyPenalty: -1 },      // 网络稍差就降低并发
            bad: { successRate: 0.60, concurrencyPenalty: -2 }        // 网络很差时大幅降低
        },

        // 带宽友好策略
        bandwidthFriendly: {
            enabled: true,
            maxBandwidthUsage: 0.7,      // 最多使用70%的可用带宽
            adaptiveChunkSize: true,      // 根据网络状况调整分片大小
            respectOtherTraffic: true     // 为其他网络流量预留空间
        }
    },

    // API 端点
    endpoints: {
        slice: '/yasubit/upload/slice',
        status: '/yasubit/upload/task/status',
        download: 'https://test.yasubit.com:8443/compressed-images/'
    }
};

// 根据文件类型获取配置
export function getUploadConfig(fileType) {
    return UPLOAD_CONFIG[fileType] || UPLOAD_CONFIG.image;
}

// 根据用户类型获取限制
export function getUserLimits(fileType, isGuest = true) {
    const config = getUploadConfig(fileType);
    return isGuest ? config.guest : null; // 登录用户暂时没有限制
}

// 检查文件是否支持
export function isSupportedFile(file, fileType) {
    const config = getUploadConfig(fileType);
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    const mimeType = file.type.toLowerCase();

    return config.supportedFormats.includes(extension) ||
        config.supportedMimeTypes.includes(mimeType);
}

// 动态调整配置
export function adjustConfigForNetwork(config, networkStats) {
    if (!UPLOAD_CONFIG.network.adaptiveConcurrency) {
        return config;
    }

    const { successRate } = networkStats;
    const { networkQuality } = UPLOAD_CONFIG.network;

    let adjustment = 0;
    if (successRate >= networkQuality.excellent.successRate) {
        adjustment = networkQuality.excellent.concurrencyBonus;
    } else if (successRate >= networkQuality.good.successRate) {
        adjustment = networkQuality.good.concurrencyBonus;
    } else if (successRate >= networkQuality.poor.successRate) {
        adjustment = networkQuality.poor.concurrencyPenalty;
    } else {
        adjustment = -2; // 网络很差时大幅降低并发
    }

    return {
        ...config,
        maxConcurrent: Math.max(1, Math.min(6, config.maxConcurrent + adjustment))
    };
}